import { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Trash, Plus, RefreshCw, Loader2 } from "lucide-react";
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from 'sonner';

interface HubspotContact {
  id: string;
  email: string;
  firstname: string;
  lastname: string;
  phone: string;
  mobilephone: string;
  jobtitle: string;
  industry?: string;
}

interface CustomerContact {
  id: string;
  customer_id: string;
  first_name: string;
  last_name: string | null;
  email: string;
  phone: string | null;
  is_active?: boolean;
  job_title?: string | null;
  industry?: string;
}

interface CustomerContactsTableProps {
  customerId: string;
  refreshTrigger?: number;
}

export default function CustomerContactsTable({ 
  customerId, 
  refreshTrigger = 0,
}: CustomerContactsTableProps) {
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [internalRefreshTrigger, setInternalRefreshTrigger] = useState(0);
  
  // Estado para o modal de sincronização
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [contactToSync, setContactToSync] = useState<CustomerContact | null>(null);
  const [hubspotContact, setHubspotContact] = useState<HubspotContact | null>(null);

  useEffect(() => {
    fetchContacts();
  }, [customerId, refreshTrigger, internalRefreshTrigger]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('customer_contacts')
        .select('*')
        .eq('customer_id', customerId)
        .eq('is_active', true);

      if (error) throw error;
      setContacts(data || []);
    } catch (error) {
      console.error('Erro ao buscar contatos:', error);
      toast.error('Não foi possível carregar os contatos do cliente.');
    } finally {
      setLoading(false);
    }
  };

  const handleInactivate = async (id: string) => {
    try {
      const { error } = await supabase
        .from('customer_contacts')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;
      
      toast.success('O contato foi inativado com sucesso.');
      
      setInternalRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Erro ao inativar contato:', error);
      toast.error('Não foi possível inativar o contato. Por favor, tente novamente.');
    }
  };

  // Função para abrir o modal de sincronização
  const openSyncModal = async (contact: CustomerContact) => {
    setContactToSync(contact);
    setIsSyncModalOpen(true);
    
    try {
      setIsSyncing(true);
      console.log('Buscando contato no HubSpot para:', contact.email);
      
      // Primeiro, buscar o ID externo associado a este contato
      const { data: externalIdData, error: externalIdError } = await supabase
        .from('external_ids')
        .select('external_id')
        .eq('entity_id', contact.id)
        .eq('entity_name', 'customer_contacts')
        .eq('system_name', 'hubspot')
        .maybeSingle();
      
      // Se encontrar um ID externo, buscar o contato do Hubspot correspondente
      if (!externalIdError && externalIdData && externalIdData.external_id) {
        console.log('ID externo encontrado:', externalIdData.external_id);
        
        const { data: hubspotData, error: hubspotError } = await supabase
          .from('hubspotcontacts')
          .select('*')
          .eq('id', externalIdData.external_id)
          .maybeSingle();
          
        if (!hubspotError && hubspotData) {
          console.log('Contato encontrado via ID externo:', hubspotData);
          setHubspotContact(hubspotData);
          setIsSyncing(false);
          return;
        } else {
          console.log('Erro ao buscar por ID externo, tentando fallback:', hubspotError);
        }
      } else {
        console.log('Nenhum ID externo encontrado, tentando buscar por email');
      }
      
      // Fallback 1: buscar por email exato
      const { data: dataByEmail, error: errorByEmail } = await supabase
        .from('hubspotcontacts')
        .select('*')
        .eq('email', contact.email)
        .maybeSingle();
        
      if (!errorByEmail && dataByEmail) {
        console.log('Contato encontrado via email exato:', dataByEmail);
        setHubspotContact(dataByEmail);
        setIsSyncing(false);
        return;
      }
      
      // Fallback 2: buscar por nome + sobrenome
      if (contact.first_name && contact.last_name) {
        const { data: dataByName, error: errorByName } = await supabase
          .from('hubspotcontacts')
          .select('*')
          .eq('firstname', contact.first_name)
          .eq('lastname', contact.last_name)
          .maybeSingle();
          
        if (!errorByName && dataByName) {
          console.log('Contato encontrado via nome completo:', dataByName);
          setHubspotContact(dataByName);
          setIsSyncing(false);
          return;
        }
      }
      
      // Fallback 3: buscar por parte do email (username)
      const emailUsername = contact.email.split('@')[0];
      if (emailUsername && emailUsername.length > 3) {
        const { data: dataByPartialEmail, error: errorByPartialEmail } = await supabase
          .from('hubspotcontacts')
          .select('*')
          .ilike('email', `%${emailUsername}%`)
          .limit(1);
          
        if (!errorByPartialEmail && dataByPartialEmail && dataByPartialEmail.length > 0) {
          console.log('Contato encontrado via email parcial:', dataByPartialEmail[0]);
          setHubspotContact(dataByPartialEmail[0]);
          setIsSyncing(false);
          return;
        }
      }
      
      // Fallback 4: Buscar no customer_id do proprietário do contato
      const { data: companyContacts, error: companyError } = await supabase
        .from('hubspotcontacts')
        .select('*')
        .eq('company_id', customerId)
        .limit(10);
        
      if (!companyError && companyContacts && companyContacts.length > 0) {
        // Tentar encontrar correspondência mais próxima
        const potentialMatch = companyContacts.find(hc => 
          (hc.firstname?.toLowerCase() === contact.first_name?.toLowerCase()) ||
          (hc.lastname?.toLowerCase() === contact.last_name?.toLowerCase()) ||
          (hc.email?.includes(emailUsername))
        );
        
        if (potentialMatch) {
          console.log('Contato encontrado via empresa:', potentialMatch);
          setHubspotContact(potentialMatch);
          setIsSyncing(false);
          return;
        }
        
        // Se não encontrar correspondência, usar o primeiro contato da empresa
        console.log('Usando primeiro contato da empresa como sugestão:', companyContacts[0]);
        setHubspotContact(companyContacts[0]);
        setIsSyncing(false);
        return;
      }
      
      // Nenhum contato encontrado
      console.log('Nenhum contato encontrado no HubSpot');
      setHubspotContact(null);
    } catch (error) {
      console.error('Erro ao buscar dados do HubSpot:', error);
      setHubspotContact(null);
    } finally {
      setIsSyncing(false);
    }
  };

  // Função para sincronizar contato com o CRM
  const syncWithCRM = async () => {
    if (!contactToSync || !hubspotContact) {
      toast.error('Não há dados do HubSpot para sincronizar.');
      return;
    }
    
    try {
      setIsSyncing(true);
      
      // Atualizar o contato com os dados do HubSpot
      const { error } = await supabase
        .from('customer_contacts')
        .update({
          first_name: hubspotContact.firstname || contactToSync.first_name,
          last_name: hubspotContact.lastname || contactToSync.last_name,
          phone: hubspotContact.mobilephone || hubspotContact.phone || contactToSync.phone,
          job_title: hubspotContact.jobtitle || contactToSync.job_title,
          industry: hubspotContact.industry || contactToSync.industry,
        })
        .eq('id', contactToSync.id);
      
      if (error) throw error;
      
      // Verificar se já existe relação na tabela external_ids
      const { data: externalIdData, error: externalIdError } = await supabase
        .from('external_ids')
        .select('id')
        .eq('entity_id', contactToSync.id)
        .eq('entity_name', 'customer_contacts')
        .eq('system_name', 'hubspot')
        .maybeSingle();
      
      // Se não existir, criar relação
      if (!externalIdData && !externalIdError) {
        const { error: createError } = await supabase
          .from('external_ids')
          .insert({
            entity_name: 'customer_contacts',
            entity_id: contactToSync.id,
            system_name: 'hubspot',
            external_id: hubspotContact.id
          });
        
        if (createError) {
          console.error('Erro ao criar vínculo com HubSpot:', createError);
        }
      }
      
      toast.success('Contato sincronizado com sucesso!');
      setIsSyncModalOpen(false);
      setInternalRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Erro ao sincronizar contato:', error);
      toast.error('Não foi possível sincronizar o contato. Por favor, tente novamente.');
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between mb-4">
        <h3 className="text-lg font-extrabold">Contatos do Cliente</h3>
      </div>

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <div className="border rounded-md">
          {contacts.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              Nenhum contato cadastrado
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead className="w-[100px]">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow key={contact.id}>
                    <TableCell>
                      {contact.first_name} {contact.last_name || ''}
                    </TableCell>
                    <TableCell>{contact.email}</TableCell>
                    <TableCell>{contact.phone || '-'}</TableCell>
                    <TableCell>{contact.job_title || '-'}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button aria-haspopup="true" size="icon" variant="ghost">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Toggle menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Ações</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => openSyncModal(contact)}>
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Sincronizar com CRM
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleInactivate(contact.id)}>
                            <Trash className="mr-2 h-4 w-4" />
                            Inativar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      {/* Sync Modal */}
      <Dialog open={isSyncModalOpen} onOpenChange={setIsSyncModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Sincronizar Contato com CRM</DialogTitle>
            <DialogDescription>
              {isSyncing ? 'Buscando dados do HubSpot...' : 
               hubspotContact ? 'Dados encontrados no HubSpot. Deseja atualizar este contato?' : 
               'Não foram encontrados dados no HubSpot para este contato.'}
            </DialogDescription>
          </DialogHeader>
          
          {isSyncing ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : hubspotContact ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-extrabold text-sm">Nome</h3>
                    <p>{hubspotContact.firstname || '-'} {hubspotContact.lastname || ''}</p>
                  </div>
                  <div>
                    <h3 className="font-extrabold text-sm">Email</h3>
                    <p>{hubspotContact.email || '-'}</p>
                  </div>
                  <div>
                    <h3 className="font-extrabold text-sm">Telefone</h3>
                  <p>{hubspotContact.mobilephone || hubspotContact.phone || '-'}</p>
                  </div>
                  <div>
                    <h3 className="font-extrabold text-sm">Cargo</h3>
                    <p>{hubspotContact.jobtitle || '-'}</p>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsSyncModalOpen(false)}>Cancelar</Button>
                <Button onClick={syncWithCRM} disabled={isSyncing}>
                  {isSyncing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Sincronizar
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="py-4 text-center text-muted-foreground">
                Nenhum dado encontrado no HubSpot para o email: {contactToSync?.email}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsSyncModalOpen(false)}>Fechar</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
} 